import { z } from "zod"
import { connectNestConnection, createNestClient, nestClient, type NestClient } from "../nest/client"
import { clientToolContent, type ClientToolContext } from "../nest/clientTools"
import { APP_CLIENT_TOOLS, APP_OPERATIONS } from "../wildlark/app-generated"
import type { AgentAuthorizeResult, AgentIdentity } from "../wildlark/appClient"
import { createComputerBackend } from "./backend"

const operations = new Map(APP_OPERATIONS.map((operation) => [String(operation.name), operation]))
let activeComputerCalls = 0

async function authorize(
    context: ClientToolContext,
    module: string | undefined,
    client: NestClient
): Promise<{ nodeId: string | null; actor?: AgentIdentity }> {
    const nodeId = client.getDiagnostics().nodeId
    const options = { signal: context.signal }
    let actor: AgentIdentity | undefined
    for (const required of module ? ["computers", module] : ["computers"]) {
        const result = await client.requestModuleMethod<AgentAuthorizeResult>(
            "wildlark.agent.authorize",
            { sessionId: context.sessionId, module: required },
            options
        )
        if (!result.authorized) throw new Error("Computer Admin access was revoked")
        actor = result.actor
    }
    context.signal.throwIfAborted()
    if (nodeId !== client.getDiagnostics().nodeId) throw new Error("The originating computer changed during this tool call")
    return { nodeId, actor }
}

async function onComputer<T>(nodeId: string, context: ClientToolContext, action: (client: NestClient) => Promise<T>): Promise<T> {
    if (activeComputerCalls >= 3) throw new Error("Three computer operations are already running. Retry after one finishes.")
    const backend = createComputerBackend()
    const client = createNestClient()
    activeComputerCalls += 1
    const cancel = () => client.close()
    context.signal.addEventListener("abort", cancel, { once: true })
    try {
        context.signal.throwIfAborted()
        const connection = await backend.getConnection(nodeId)
        context.signal.throwIfAborted()
        await connectNestConnection(client, connection, { timeoutMs: 6_000 })
        context.signal.throwIfAborted()
        return await action(client)
    } finally {
        activeComputerCalls -= 1
        context.signal.removeEventListener("abort", cancel)
        client.close()
    }
}

export function installComputerAgentTools(client: NestClient = nestClient): () => void {
    const dispose = [
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.computers.list,
                description:
                    "List registered computers without changing the selected computer. Returns descriptors, never credentials. Requires this thread's Computers app access.",
                schema: z.object({}).strict(),
            },
            async (_input, context) => {
                await authorize(context, undefined, client)
                const state = await createComputerBackend().list()
                return {
                    activeNodeId: state.activeNodeId,
                    computers: state.computers.map((computer) => ({ nodeId: computer.nodeId, name: computer.name, kind: computer.kind })),
                }
            }
        ),
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.computers.describe,
                description:
                    "Inspect a registered computer's identity, capabilities, and current resource state through a temporary connection. An offline computer returns an error.",
                schema: z.object({ nodeId: z.string().min(1) }).strict(),
            },
            async (input, context) => {
                await authorize(context, undefined, client)
                return onComputer(input.nodeId, context, async (client) => {
                    const options = { signal: context.signal }
                    const identity = await client.requestMethod("system.initialize", { protocolVersion: 1, clientName: "WildLark app control" }, options)
                    const metrics = await client.requestMethod("system.metrics.read", {}, options)
                    return clientToolContent([
                        { type: "text", text: JSON.stringify({ nodeId: identity.nodeId, capabilities: identity.capabilities, metrics }) },
                    ])
                })
            }
        ),
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.computers.call,
                description:
                    "Execute an installed WildLark app operation on an explicit registered computer. Use the app-tools catalog for operation names and input schemas. Only this thread's enabled modules are available; read-only turns cannot mutate. Does not change the user's selected computer.",
                schema: z.object({ nodeId: z.string().min(1), operation: z.string().min(1), input: z.record(z.unknown()) }).strict(),
            },
            async (input, context) => {
                const operation = operations.get(input.operation)
                if (!operation) throw new Error(`Unknown app operation: ${input.operation}`)
                if (context.mode === "read-only" && operation.effect !== "read") throw new Error("Read-only threads cannot change another computer")
                const origin = await authorize(context, operation.module, client)
                let operationInput = input.input
                if (operation.name === "process.start" || operation.name === "process.batch.start") {
                    if (!origin.nodeId || !origin.actor?.taskId || !origin.actor.workspaceId || origin.actor.sessionId !== context.sessionId) {
                        throw new Error("The originating Nest must provide task identity for process launches. Update it and retry.")
                    }
                    const labels = z.record(z.string()).optional().parse(input.input.labels) ?? {}
                    delete labels["wildlark.taskTitle"]
                    operationInput = {
                        ...input.input,
                        labels: {
                            ...labels,
                            "wildlark.taskId": origin.actor.taskId,
                            "wildlark.workspaceId": origin.actor.workspaceId,
                            "wildlark.sessionId": origin.actor.sessionId,
                            "wildlark.originNodeId": origin.nodeId,
                        },
                    }
                }
                return onComputer(input.nodeId, context, async (client) => {
                    const result = await client.requestModuleMethod<unknown>(
                        "wildlark.app.invoke",
                        { operation: input.operation, input: operationInput, mode: context.mode, permissionMode: context.permissionMode },
                        { signal: context.signal }
                    )
                    // The client-tool boundary converts the JSON RPC response into
                    // content without weakening its generic JsonValue contract.
                    return clientToolContent([{ type: "text", text: JSON.stringify({ nodeId: input.nodeId, operation: input.operation, result }) }])
                })
            }
        ),
    ]
    return () => {
        for (const stop of dispose.reverse()) stop()
    }
}
